import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Nav from "@/components/Nav";
import { PRODUCT_CATS } from "@/components/Nav";
import { SupportContactInline } from "@/components/SupportContact";
import { ArrowRight, CheckCircle, Lightning, Palette } from "@phosphor-icons/react";

// Category-specific marketing content — kept as data so the same view renders any category.
const CONTENT = {
  "visiting-cards": {
    headline: "Visiting cards that get remembered.",
    subhead: "350 gsm matte or gloss, spot UV, foil, embossing — production-ready in 48 hours.",
    hero_bg: "from-rose-100 to-white",
    starts_at: "₹499 / 100 cards",
    starting_tags: ["350 GSM matte", "Free spot-UV upgrade", "Round-corner option"],
    features: ["300+ AI-generated design starts", "Free 4 logo concepts (Logo Studio)", "Free die-cut for round corners", "Delivered pan-India in 5 days"],
    template_hint: "Logo Studio → pick a logo → auto-lay it onto 8 card layouts.",
  },
  "brochures": {
    headline: "Brochures your sales team actually uses.",
    subhead: "Bi-fold, tri-fold, gate-fold and Z-fold — with editable AI-generated hero visuals.",
    hero_bg: "from-blue-100 to-white",
    starts_at: "₹1,299 / 100 brochures",
    starting_tags: ["A4 & Legal sizes", "170 gsm silk art", "Fold + score included"],
    features: ["AI hero images tuned for your industry", "Editable copy blocks by our copy team", "3D flatlay mockup on request", "Optional QR-code embed"],
    template_hint: "Send us a brief → get 3 layouts in 24h.",
  },
  "hoardings": {
    headline: "Hoardings, flex banners, and standees.",
    subhead: "Large-format outdoor at true-print resolution — no jaggies, no colour drift.",
    hero_bg: "from-amber-100 to-white",
    starts_at: "₹35 / sq ft",
    starting_tags: ["12mil frontlit flex", "Star flex option", "Eyelets & pole pockets"],
    features: ["Pantone match on solid colours", "Design proofs in 6 hours", "Nationwide delivery + install partners", "Weather-tested inks"],
    template_hint: "Upload your existing artwork or let AI redraw it at outdoor scale.",
  },
  "packaging": {
    headline: "Retail packaging & mailer boxes.",
    subhead: "Print + die-cut + assembly. Or grab a free dieline template and print anywhere.",
    hero_bg: "from-emerald-100 to-white",
    starts_at: "₹18 / mailer box",
    starting_tags: ["Corrugated + cartons", "Custom sizes free", "Print inside + outside"],
    features: ["12 free die-cut template families (SVG/PDF/DXF)", "Live 3D fold preview", "Sample pack shipped free", "Minimums start at 100 units"],
    template_hint: "Or use our free Die-Cut Studio: pick a shape → set L×W×H → download the flat pattern.",
    cta_extra: { to: "/dielines", label: "Free die-cut studio →" },
  },
  "apparel": {
    headline: "Apparel — T-shirts, hoodies, caps.",
    subhead: "AI-designed graphics + screen or DTF printing at production quantities.",
    hero_bg: "from-purple-100 to-white",
    starts_at: "₹249 / T-shirt",
    starting_tags: ["Bio-wash cotton", "DTF + screen print", "Size XS – XXL"],
    features: ["AI-generated graphics (Free with Logo Studio)", "Sample piece before bulk", "Neck-label & tag customisation", "Bulk discounts from 50 units"],
    template_hint: "Send your logo → we lay it on 6 apparel mockups in 24h.",
  },
  "corporate-gifts": {
    headline: "Corporate gift hampers & merch.",
    subhead: "Branded diaries, mugs, water bottles, tote bags — curated hampers for Diwali & new-hire kits.",
    hero_bg: "from-orange-100 to-white",
    starts_at: "₹499 / employee",
    starting_tags: ["150+ items catalogue", "Custom hamper design", "Sequential gift-tagging"],
    features: ["Curated hampers from ₹499 to ₹15,000", "Bulk personalisation (each item named)", "Nationwide delivery to employee homes", "GST-compliant invoicing"],
    template_hint: "Tell us your budget + head-count — get 3 hamper themes in 24h.",
  },
  "stationery": {
    headline: "Corporate stationery, done properly.",
    subhead: "Letterheads, envelopes, invoices, notepads — every touchpoint on the same identity system.",
    hero_bg: "from-slate-100 to-white",
    starts_at: "₹2,999 / starter pack",
    starting_tags: ["100 gsm bond paper", "Watermark option", "Digital + offset"],
    features: ["Consistent identity across every touchpoint", "GSTIN + registered address auto-typeset", "Multi-branch address variants", "Compact starter packs from 100 each"],
    template_hint: "Upload your identity brief → get 8 stationery items designed together.",
  },
  "certificates": {
    headline: "Certificates & appointment letters.",
    subhead: "Metallic foils, embossing, holographic seals — the print details that make a certificate feel real.",
    hero_bg: "from-yellow-100 to-white",
    starts_at: "₹149 / certificate",
    starting_tags: ["Gold + silver foil", "Blind embossing", "Serial-numbered"],
    features: ["Bulk personalisation (VDP)", "Anti-forgery seals & holograms", "Digital signatures + printed", "Ships in gift-tube option"],
    template_hint: "Send us the recipient list — we personalise every certificate.",
  },
};

export default function ProductCategory() {
  const { slug } = useParams();
  const cat = PRODUCT_CATS.find(c => c.slug === slug);
  const content = CONTENT[slug];

  useEffect(() => {
    if (cat) document.title = `${cat.label} — Print, Design & Delivery | Talaio/AI`;
  }, [cat]);

  if (!cat || !content) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[var(--pf-bg)]">
      <Nav />

      {/* Hero */}
      <section className={`bg-gradient-to-b ${content.hero_bg}`}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 pt-16 pb-20">
          <div className="text-xs font-bold uppercase tracking-widest text-[var(--pf-brand)] mb-4 flex items-center gap-2">
            <cat.icon size={16} weight="duotone"/>
            <span>Products · {cat.label}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mb-4 max-w-3xl">
            {content.headline}
          </h1>
          <p className="text-base md:text-lg text-[var(--pf-text-2)] max-w-2xl mb-8">
            {content.subhead}
          </p>
          <div className="flex flex-wrap gap-3 mb-8">
            <Link to="/brief/new" data-testid="prod-cta-primary" className="pf-btn-primary">
              Get a quote in 60 seconds <ArrowRight size={16} weight="bold" className="inline ml-1"/>
            </Link>
            {content.cta_extra ? (
              <Link to={content.cta_extra.to} data-testid="prod-cta-extra" className="pf-btn-secondary">
                {content.cta_extra.label}
              </Link>
            ) : (
              <Link to="/logo-studio" data-testid="prod-cta-extra" className="pf-btn-secondary">
                Free logo first →
              </Link>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="px-3 py-1.5 bg-white/60 rounded-full text-xs font-bold uppercase tracking-wider text-[var(--pf-text-2)] backdrop-blur">
              Starts at {content.starts_at}
            </span>
            {content.starting_tags.map(t => (
              <span key={t} className="px-3 py-1.5 bg-white/60 rounded-full text-xs font-semibold text-[var(--pf-text-2)] backdrop-blur">
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <div className="text-xs pf-label mb-2">What's included</div>
            <h2 className="text-2xl md:text-3xl font-black tracking-tight mb-6">
              Everything, done for you.
            </h2>
            <ul className="space-y-4">
              {content.features.map(f => (
                <li key={f} className="flex gap-3">
                  <CheckCircle size={22} weight="fill" className="text-[var(--pf-brand)] shrink-0 mt-0.5"/>
                  <span className="text-[var(--pf-text)] leading-relaxed">{f}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="border border-[var(--pf-border)] rounded-lg p-6 md:p-8 bg-gradient-to-br from-white to-[var(--pf-surface-2)]/50">
            <div className="text-xs pf-label mb-2 flex items-center gap-1.5">
              <Lightning size={12} weight="fill"/> How it works
            </div>
            <div className="space-y-6 mt-6">
              {[
                { n: 1, t: "Tell us what you want", d: "Fill a 60-second brief with your brand, quantity, and delivery date. Or ask AI to generate options first." },
                { n: 2, t: "Get 3 options in 24 hours", d: content.template_hint },
                { n: 3, t: "Approve & we produce", d: "Pay 50% to start, 50% before dispatch. GST invoice included." },
                { n: 4, t: "Delivered pan-India", d: "5-7 business days for most cities. Rush ship in 48h at 1.5× cost." },
              ].map(step => (
                <div key={step.n} className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-[var(--pf-brand)] text-white font-black text-sm flex items-center justify-center shrink-0">
                    {step.n}
                  </div>
                  <div>
                    <div className="font-bold text-sm mb-1">{step.t}</div>
                    <div className="text-sm text-[var(--pf-text-2)] leading-relaxed">{step.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Other categories */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 pb-20">
        <div className="text-xs pf-label mb-4">Also available</div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          {PRODUCT_CATS.filter(c => c.slug !== slug).map(c => (
            <Link
              key={c.slug}
              to={`/products/${c.slug}`}
              data-testid={`prod-related-${c.slug}`}
              className="flex flex-col items-center gap-2 p-4 border border-[var(--pf-border)] rounded-lg bg-white hover:border-[var(--pf-brand)] hover:shadow-md transition-all"
            >
              <c.icon size={24} weight="duotone" className="text-[var(--pf-brand)]"/>
              <span className="text-[11px] font-bold uppercase tracking-widest text-center leading-tight">{c.label}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Support */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 pb-24">
        <div className="border border-[var(--pf-border)] rounded-lg p-8 md:p-12 bg-gradient-to-br from-[var(--pf-brand)]/10 to-transparent">
          <div className="flex items-center gap-2 text-xs pf-label mb-3">
            <Palette size={14} weight="duotone"/> Not sure where to start?
          </div>
          <h2 className="text-2xl md:text-3xl font-black tracking-tight mb-4">
            Chat with a real designer.
          </h2>
          <p className="text-[var(--pf-text-2)] mb-6 max-w-2xl">
            We reply on WhatsApp in under 5 minutes during business hours.
            Send us your brief, budget, and delivery date — we'll come back with mockups.
          </p>
          <SupportContactInline />
        </div>
      </section>
    </div>
  );
}
